"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • USDA FSIS — Refrigeration and Food Safety: keep refrigerator at 40°F
//     or below; freezer at 0°F. A failed door gasket lets the unit drift
//     above these set points.
//   • Whirlpool, GE, LG, Samsung, Frigidaire service & use-and-care guides:
//     dollar-bill / paper test (industry-standard service test);
//     replace gasket if the bill slides out with no drag, if the gasket is
//     torn, or if condensation forms on the door frame.
//   • DOE / ENERGY STAR refrigerator guidance: failing door seals are a
//     common cause of elevated electricity use; gasket replacement is one
//     of the highest-ROI maintenance tasks.

const SECTIONS = [
  { id: "what", t: "What the seal does" },
  { id: "test", t: "How to test" },
  { id: "fail", t: "Reading the result" },
  { id: "fix", t: "Fix or replace" },
  { id: "prevent", t: "Prevention" },
] as const

export function DoorSealTestCalculatorPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Door Seal Test"
      heroEyebrow="Maintenance"
      heroTitlePre="The 30-second test that"
      heroTitleItalic="catches"
      heroTitlePost="a failing gasket."
      heroSubtitle="A door gasket that no longer seals lets warm, humid air leak in around the clock — driving up energy use and pushing the interior toward USDA's 40°F danger zone. Here's the same test refrigerator service techs use, plus what to do with the result."
      heroMeta={["⏱ ~3 min read", "USDA + manufacturer service tests"]}
      heroShare={<ToolShareBar title="Refrigerator Door Seal Test" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The dollar-bill test
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Close a dollar bill in the door. If you can pull it out{" "}
            <em className="italic text-[#ff9951]">with no drag</em>, the gasket isn&apos;t
            sealing.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Safe fridge
              </div>
              <div className="text-[22px] font-black tracking-tight">≤ 40°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Safe freezer
              </div>
              <div className="text-[22px] font-black tracking-tight">0°F</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "Is the dollar-bill test really accurate?",
          answer:
            "It's the standard test referenced in residential refrigerator service guides for a reason: a healthy magnetic gasket should grip a slip of paper firmly the entire way around the door. Any spot where it slides out without resistance is a leak path. It's a screening test — for a definitive measurement, a service tech can run an interior smoke test or measure case temperature drift.",
        },
        {
          question: "How often should I run it?",
          answer:
            "Every 6 months, and after any cleaning that disturbed the gasket. DOE residential maintenance guidance treats gasket inspection as part of the same 6-month interval as condenser-coil cleaning.",
        },
        {
          question: "How long do refrigerator door gaskets last?",
          answer:
            "Most consumer-grade gaskets last 8–10 years. Heavy-use households (especially with kids) often see them fail earlier — by year 5–6. UV exposure, harsh cleaners, and door slamming all shorten gasket life.",
        },
        {
          question: "Can I clean a stiff or sticky gasket back to working order?",
          answer:
            "Sometimes. Wipe with warm soapy water (no bleach, no ammonia, no abrasive cleaners — they degrade the rubber per manufacturer guides). A thin coat of food-grade silicone or petroleum jelly on the sealing surface can restore pliability. If the rubber has hardened, cracked, or torn, it needs replacing.",
        },
        {
          question: "How much does a replacement gasket cost?",
          answer:
            "Typical OEM gasket: $40–$120 for the part, depending on model and door size. Most are owner-replaceable in 30–60 minutes — check your service manual for whether it's screwed in, snapped in, or held by a retainer.",
        },
      ]}
      relatedTools={[
        { title: "Temperature Settings Guide", category: "Temperature", description: "USDA reference set points.", href: "/tools/refrigerator-temperature-settings-guide", time: "~5 min" },
        { title: "Maintenance Schedule", category: "Maintenance", description: "When to do every other task.", href: "/tools/refrigerator-maintenance-schedule", time: "~5 min" },
        { title: "Energy Cost Calculator", category: "Energy & Cost", description: "What a leaking seal costs you.", href: "/tools/energy-cost-calculator", time: "~30 sec" },
        { title: "Refrigerator Lifespan", category: "Specialty", description: "Years left on the unit.", href: "/tools/refrigerator-lifespan-calculator", time: "~45 sec" },
      ]}
      cta={{
        title: "Gasket gone, condenser noisy, ice production weak?",
        body: "When more than one system is failing, repair often costs more than the unit is worth. Browse french-door refrigerators and compare against your repair quote.",
        buttonLabel: "Browse french-door refrigerators",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          <article>
            <GuideTool
              eyebrow="Interactive diagnostic"
              title="Run the seal test — get a verdict"
              intro="Do the dollar-bill test on your door, look the gasket over, then answer three questions. The verdict applies the same thresholds the service guides below use."
              questions={[
                {
                  id: "bill",
                  label: "Close a dollar bill in the door and pull. What happens?",
                  options: [
                    { value: "firm", label: "Firm drag everywhere" },
                    { value: "spots", label: "Slides out easily at some spots" },
                    { value: "falls", label: "Falls out or no drag at all" },
                  ],
                },
                {
                  id: "rubber",
                  label: "How does the gasket rubber look and feel?",
                  options: [
                    { value: "soft", label: "Soft and intact" },
                    { value: "stiff", label: "Stiff, sticky, or flattened" },
                    { value: "torn", label: "Cracked or torn" },
                  ],
                },
                {
                  id: "symptom",
                  label: "Any of these symptoms?",
                  options: [
                    { value: "none", label: "None" },
                    { value: "condensation", label: "Condensation or frost at the door frame" },
                    { value: "runs", label: "Compressor runs nearly constantly" },
                  ],
                },
              ]}
              compute={(a) => {
                if (a.bill === "falls" || a.rubber === "torn") {
                  return {
                    tone: "bad",
                    verdict: "Replace the gasket",
                    detail:
                      "A bill with no drag, or cracked/torn rubber, means the magnetic seal is gone — warm air is leaking in around the clock. A typical OEM gasket runs $40–$120 and most are owner-replaceable in 30–60 minutes.",
                    action: { label: "Estimate the repair cost", href: "/tools/refrigerator-gasket-replacement-cost" },
                  }
                }
                if (a.bill === "spots" || a.rubber === "stiff" || a.symptom !== "none") {
                  return {
                    tone: "warn",
                    verdict: "Recondition and retest",
                    detail:
                      "Weak spots or stiff rubber can often be recovered: wash with warm soapy water (never bleach or abrasives), work in a thin coat of food-grade silicone, and rerun the bill test at the weak spots. If the drag does not return, replace.",
                  }
                }
                return {
                  tone: "good",
                  verdict: "Your seal is healthy",
                  detail:
                    "Firm drag the whole way around with soft, intact rubber is exactly what a working gasket feels like. Retest every 6 months — the same interval as condenser-coil cleaning.",
                }
              }}
              ctaLabel="Get my verdict"
              footnote="Thresholds: USDA FSIS 40°F guidance + the manufacturer service-guide dollar-bill test."
            />
            <section id="what" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What the door seal actually does
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                The gasket is a hollow magnetic strip that mates the door to the cabinet. When
                it&apos;s healthy, it makes an airtight seal — the compressor cycles infrequently
                because warm room air can&apos;t reach the cold interior.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                When it fails, three things happen at once: the compressor runs longer to fight
                the leak, the interior drifts upward (toward and past USDA&apos;s 40°F threshold
                for safe refrigeration), and condensation forms on or around the door frame as
                humid air contacts cold surfaces.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    USDA 40°F line
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    A leaking gasket can push fridge temps above USDA&apos;s 40°F safe-refrigeration
                    threshold — especially in summer or in hot kitchens. Test the seal before you
                    chase a dying compressor.
                  </p>
                </div>
              </div>
            </section>

            <section id="test" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                The test, step by step
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Wipe the gasket and door frame", d: "Use a damp cloth with warm soapy water. Crumbs, sticky residue, and dust can fool the test by holding the bill in place where the rubber isn't actually sealing." },
                  { n: "02", t: "Close a dollar bill in the door", d: "Place a US dollar bill (or any thin paper of similar stiffness) so half is inside, half outside. Shut the door normally — don't slam it." },
                  { n: "03", t: "Pull the bill straight out", d: "A healthy gasket grips firmly — you should feel real drag, and the bill may resist or tear before sliding free." },
                  { n: "04", t: "Repeat at every position", d: "Top corners, sides, bottom corners, and across the bottom. The most common failure points are the lower hinge corner and the bottom edge — start there if you're skipping anywhere." },
                  { n: "05", t: "Check for visible damage", d: "Look for tears, splits, hardening, or permanent deformation. A torn gasket fails the test and needs replacement regardless of grip elsewhere." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section id="fail" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Reading the result
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        What you observed
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Verdict
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Strong drag everywhere", "Healthy seal", "No action — recheck in 6 months"],
                      ["Drag at most points, slips at one spot", "Localized failure", "Try cleaning + reshaping; retest"],
                      ["Slips out at multiple spots", "Gasket failing", "Replace gasket"],
                      ["Visible tear, split, or hardening", "Gasket failed", "Replace gasket"],
                      ["Condensation on door frame even when closed", "Air leak around seal", "Replace gasket; check door alignment"],
                      ["Door doesn't auto-close from 45° open", "Hinge or level issue (not gasket)", "Re-level the unit (see leveling guide)"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-600">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section id="fix" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Fix or replace
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Three escalating responses, ordered by effort. Manufacturer service guides treat
                replacement as the only durable fix once the rubber has lost shape.
              </p>
              <div className="space-y-3">
                {[
                  { t: "Clean and recondition", d: "Wipe with warm soapy water. Apply a thin coat of food-grade silicone or petroleum jelly to the sealing surface. Avoid bleach, ammonia, and abrasive cleaners — all degrade the rubber." },
                  { t: "Reshape a deformed gasket", d: "If a section was crushed or kinked, run a hair dryer on low heat over the deformed area while pressing it back into shape. Re-test once cool." },
                  { t: "Replace the gasket", d: "Order the OEM part by your model number. Most modern gaskets snap into a retainer or are screwed in under a flange. 30–60 minutes per door for a competent DIY; longer for built-ins." },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="prevent" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Prevention checklist
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Wipe the gasket monthly with warm soapy water",
                  "Avoid bleach, ammonia, and abrasive cleaners",
                  "Don't slam the door — let the seal do its work",
                  "Keep the door frame and gasket free of food residue",
                  "Re-level the unit if doors don't self-close from 45° open",
                  "Test with the dollar-bill test every 6 months",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>
          </article>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If the seal is gone"
          title="French-door refrigerators to compare"
          description="A new door gasket seals tighter than a worn one."
          collectionType="french-door"
          fallbackCollectionType="side-by-side"
        />
      </section>
    </ToolPageShell>
  )
}
